// props.ts — non-animated city objects as one InstancedMesh per kind, plus one
// InstancedMesh for every soft ground disc. Gameplay still moves a plain
// Object3D; only roots inside the camera (root.visible / anchor.visible) are
// copied into the instance buffer, so off-screen buildings cost no vertices.
import * as THREE from "three";

interface PropEntry {
  root: THREE.Object3D;
}

class PropKind {
  readonly mesh: THREE.InstancedMesh;
  readonly entries: PropEntry[] = [];
  readonly capacity: number;

  constructor(geometry: THREE.BufferGeometry, material: THREE.Material, capacity: number, castShadow: boolean) {
    this.capacity = capacity;
    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = castShadow;
    this.mesh.receiveShadow = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  }

  add(root: THREE.Object3D) {
    if (this.entries.length >= this.capacity) return false;
    this.entries.push({ root });
    return true;
  }

  update() {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      if (this.entries[i]!.root.parent) continue;
      const last = this.entries.length - 1;
      if (i !== last) this.entries[i] = this.entries[last]!;
      this.entries.pop();
    }
    let visible = 0;
    for (let i = 0; i < this.entries.length; i++) {
      const root = this.entries[i]!.root;
      if (!root.visible) continue;
      root.updateMatrix();
      this.mesh.setMatrixAt(visible, root.matrix);
      visible++;
    }
    this.mesh.count = visible;
    this.mesh.instanceMatrix.needsUpdate = visible > 0;
  }
}

export class PropBatch {
  readonly group = new THREE.Group();
  private readonly kinds = new Map<number, PropKind>();

  constructor() {
    this.group.matrixAutoUpdate = false;
  }

  add(kind: number, geometry: THREE.BufferGeometry, material: THREE.Material, root: THREE.Object3D, castShadow: boolean) {
    const batch = this.kinds.get(kind);
    if (batch?.add(root)) return;
    const capacity = Math.max(64, (batch?.capacity ?? 0) * 2);
    const next = new PropKind(geometry, material, capacity, castShadow);
    if (batch) {
      for (const old of batch.entries) next.add(old.root);
      this.group.remove(batch.mesh);
      batch.mesh.dispose();
    }
    next.add(root);
    this.kinds.set(kind, next);
    this.group.add(next.mesh);
  }

  update() {
    for (const batch of this.kinds.values()) batch.update();
  }

  dispose() {
    for (const batch of this.kinds.values()) batch.mesh.dispose();
  }
}

const FLAT = new THREE.Quaternion();
FLAT.setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));

/** One draw call for every soft ground disc. Anchors stay outside the scene. */
export class BlobShadows {
  mesh: THREE.InstancedMesh;
  private entries: THREE.Object3D[] = [];
  private capacity: number;
  private readonly tmp = new THREE.Object3D();

  constructor(capacity = 512) {
    this.capacity = capacity;
    this.mesh = this.createMesh(capacity);
  }

  private createMesh(capacity: number) {
    const geometry = new THREE.CircleGeometry(1, 10);
    const material = new THREE.MeshBasicMaterial({
      color: 0x1d2932,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
    });
    const mesh = new THREE.InstancedMesh(geometry, material, capacity);
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    return mesh;
  }

  add(anchor: THREE.Object3D) {
    if (this.entries.length >= this.capacity) {
      const nextCapacity = this.capacity * 2;
      const next = this.createMesh(nextCapacity);
      const parent = this.mesh.parent;
      if (parent) {
        parent.add(next);
        parent.remove(this.mesh);
      }
      this.mesh.geometry.dispose();
      (this.mesh.material as THREE.Material).dispose();
      this.mesh.dispose();
      this.mesh = next;
      this.capacity = nextCapacity;
    }
    this.entries.push(anchor);
  }

  update() {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      if (this.entries[i]!.parent) continue;
      const last = this.entries.length - 1;
      if (i !== last) this.entries[i] = this.entries[last]!;
      this.entries.pop();
    }
    let visible = 0;
    for (let i = 0; i < this.entries.length; i++) {
      const anchor = this.entries[i]!;
      if (!anchor.visible) continue;
      const radius = (anchor.userData.radius as number) || 1;
      this.tmp.position.copy(anchor.position);
      this.tmp.quaternion.copy(FLAT);
      this.tmp.scale.set(radius, radius, 1);
      this.tmp.updateMatrix();
      this.mesh.setMatrixAt(visible, this.tmp.matrix);
      visible++;
    }
    this.mesh.count = visible;
    this.mesh.instanceMatrix.needsUpdate = visible > 0;
  }

  dispose() {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
  }
}
